import { ParsedUrlQueryInput } from 'node:querystring';

import { parse as pslParse } from 'psl';

import queryString from 'query-string';

import { captureError } from './errors';
import {
  accountsAbsoluteBaseUrl,
  cloudAbsoluteBaseUrl,
  docsAbsoluteBaseUrl,
  hubAbsoluteBaseUrl,
  websiteAbsoluteBaseUrl,
} from '../constants/url';

import type { ParsedUrlQuery } from 'querystring';
import type { UrlObject } from 'url';

/**
 * According to the documentation, in order to perform "shallow" redirect,
 * the page must stay the same and only params can change. However, since
 * we use [name] syntax for the paths, NextJS considers this as a different
 * page and ignores "shallow" parameter. This function simply replaces dynamic
 * parameters in the path with values which we pass in "query" parameter.
 */
export function generateUrl({
  hostname,
  includeHost,
  ...urlObj
}: Parameters<typeof formatUrl>[0] & {
  hash?: string;
  includeHost?: boolean;
  pathname: string;
  query?: ParsedUrlQueryInput;
}) {
  if (urlObj.query && typeof urlObj.query === 'object' && urlObj.pathname) {
    for (const key of Object.keys(urlObj.query)) {
      if (urlObj.pathname.includes(`[${key}]`) && urlObj.query[key]) {
        urlObj.pathname = urlObj.pathname.replace(`[${key}]`, `${urlObj.query[key]}`);
        delete urlObj.query[key];
      }
      if (urlObj.pathname.includes(`$${key}`) && urlObj.query[key]) {
        urlObj.pathname = urlObj.pathname.replace(`$${key}`, `${urlObj.query[key]}`);
        delete urlObj.query[key];
      }
    }
  }

  const url = formatUrl({
    ...urlObj,
    query: queryString.stringify(urlObj.query as Record<string, string>, {
      arrayFormat: 'comma',
    }),
  });

  if (/\[[a-zA-Z0-9_-]+\]/.test(url) || /\$[a-zA-Z0-9_-]+/.test(url)) {
    captureError(new Error(`Some dynamic parameters are not replaced in the pathname: ${url}`));
  }

  return (includeHost || hostname) && !url.startsWith('http')
    ? `${hostname || getAbsoluteBaseUrl()}${url}`
    : url;
}

export function getAbsoluteBaseUrl() {
  if (process.env.VERCEL_ENV === 'preview') {
    return `https://${process.env.VERCEL_URL}`;
  }

  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true'
  ) {
    return process.env.APP_NAME === 'platform'
      ? 'http://localhost:3000'
      : 'https://local.cloudquery.io:8443';
  }

  switch (process.env.APP_NAME) {
    case 'accounts': {
      return accountsAbsoluteBaseUrl;
    }
    case 'cloud': {
      return cloudAbsoluteBaseUrl;
    }
    case 'docs': {
      return docsAbsoluteBaseUrl;
    }
    case 'hub': {
      return hubAbsoluteBaseUrl;
    }
    case 'website': {
      return websiteAbsoluteBaseUrl;
    }
    case 'platform': {
      return typeof window === 'undefined' ? '' : window.location.origin;
    }
  }
}

export function getAbsoluteBaseUrlSubdomain() {
  const baseUrl = getAbsoluteBaseUrl() || '';

  const { hostname } = new URL(baseUrl);

  return hostname.split('.').length > 2 ? hostname.split('.')[0] : undefined;
}

export function getReturnToUrl(returnTo: string | null | undefined) {
  if (!returnTo) {
    return null;
  }

  // allow relative URLs - but block protocol-relative URLs (//x.y.z)
  if (returnTo.startsWith('/')) {
    if (returnTo.startsWith('//')) {
      return null;
    }

    return returnTo;
  }

  try {
    if (!isCloudQueryDomain({ url: returnTo })) {
      return null;
    }

    return returnTo;
  } catch {
    return null;
  }
}

export const isAbsoluteLink = (href: string) =>
  href?.startsWith('https://') || href?.startsWith('http://');

export const isAnchorLink = (href: string) => href?.startsWith('#');

export function getCurrentBaseUrl() {
  const { protocol } = window.location;
  const { hostname } = window.location;
  const { port } = window.location;

  let baseUrl = `${protocol}//${hostname}`;
  if (port) {
    baseUrl += `:${port}`;
  }

  return baseUrl;
}

export function isSameUrl(target: URL, current: URL) {
  const targetUrl = new URL(
    `${target.protocol}//${target.host}${target.pathname}?page=${target.searchParams.get('page')}`,
  );
  const currentUrl = new URL(
    `${current.protocol}//${current.host}${current.pathname}?page=${current.searchParams.get(
      'page',
    )}`,
  );

  return targetUrl.href === currentUrl.href;
}

export function isDomain(domain: string, params: { url: string } | { hostname: string }) {
  if ('url' in params && !isAbsoluteLink(params.url)) {
    return false;
  }

  try {
    const hostname = 'url' in params ? new URL(params.url).hostname : params.hostname;
    const parsed = pslParse(hostname);

    if ('error' in parsed) {
      return false;
    }

    return parsed.domain === domain;
  } catch {
    return false;
  }
}

export function isCloudQueryDomain(params: { url: string } | { hostname: string }) {
  return isDomain('cloudquery.io', params);
}

const slashedProtocols = /https?|ftp|gopher|file/;

function formatUrl(urlObj: UrlObject) {
  const { auth, hostname } = urlObj;
  let protocol = urlObj.protocol || '';
  let pathname = urlObj.pathname || '';
  let hash = urlObj.hash || '';
  let query = urlObj.query || '';
  let host: string | false = false;

  const parsedAuth = auth ? `${encodeURIComponent(auth).replaceAll('%3A', ':')}@` : '';

  if (urlObj.host) {
    host = parsedAuth + urlObj.host;
  } else if (hostname) {
    host = parsedAuth + (~hostname.indexOf(':') ? `[${hostname}]` : hostname);
    if (urlObj.port) {
      host += `:${urlObj.port}`;
    }
  }

  if (query && typeof query === 'object') {
    query = String(urlQueryToSearchParams(query as ParsedUrlQuery));
  }

  let search = urlObj.search || (query && `?${query}`) || '';

  if (protocol && !protocol.endsWith(':')) protocol += ':';

  if (urlObj.slashes || ((!protocol || slashedProtocols.test(protocol)) && host !== false)) {
    host = `//${host || ''}`;
    if (pathname && pathname[0] !== '/') pathname = `/${pathname}`;
  } else if (!host) {
    host = '';
  }

  if (hash && hash[0] !== '#') hash = `#${hash}`;
  if (search && search[0] !== '?') search = `?${search}`;

  pathname = pathname.replace(/[?#]/g, encodeURIComponent);
  search = search.replaceAll('#', '%23');

  return `${protocol}${host}${pathname}${search}${hash}`;
}

export function searchParamsToUrlQuery(searchParams: URLSearchParams): ParsedUrlQuery {
  const query: ParsedUrlQuery = {};
  for (const [key, value] of searchParams.entries()) {
    const existing = query[key];
    if (typeof existing === 'undefined') {
      query[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      query[key] = [existing, value];
    }
  }

  return query;
}

function stringifyUrlQueryParam(param: unknown): string {
  if (typeof param === 'string') {
    return param;
  }

  if ((typeof param === 'number' && !isNaN(param)) || typeof param === 'boolean') {
    return String(param);
  } else {
    return '';
  }
}

function urlQueryToSearchParams(query: ParsedUrlQuery): URLSearchParams {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, stringifyUrlQueryParam(item));
      }
    } else {
      searchParams.set(key, stringifyUrlQueryParam(value));
    }
  }

  return searchParams;
}

export function isRelativeUrl(url: string) {
  return url.startsWith('/') && !url.startsWith('//');
}
